import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * PARTS RISK (device check, phase 0 — 11/2/2026).
 *
 * Devices whose record could order the wrong parts: the label and the record
 * disagree, the model is on the state list as a different type, or the model
 * isn't on the state list at all. Devices on a live PO come first, then open
 * quotes. Read-only: every flag shows both values side by side (the proof),
 * and the office fixes the record from the device page.
 */

export interface PartsRiskFlag {
    kind: "model" | "model_more_specific" | "make" | "size" | "detector_type" | "model_type" | "off_list";
    text: string;
    label?: string;
    record?: string;
}

export interface PartsRiskRow {
    id: string;
    name: string | null;
    manufacturer: string | null;
    model: string | null;
    sizeLabel: string | null;
    assemblyType: string | null;
    serialNumber: string | null;
    siteId: string;
    siteName: string | null;
    city: string | null;
    customerName: string | null;
    openPo: { id: string; number: string | null } | null;
    openQuote: { id: string; number: string | null } | null;
    flags: PartsRiskFlag[];
}

export interface PartsRiskResponse {
    counts: {
        checked: number;
        label: number;
        modelType: number;
        offList: number;
        onOpenPo: number;
        onOpenQuote: number;
        total: number;
    };
    stateListLoaded: boolean;
    rows: PartsRiskRow[];
}

/** The two-sided proof for a flag: "Label | Record" or "State list | Record". */
export function flagSides(f: PartsRiskFlag): { leftHead: string; left: string; right: string } | null {
    if (f.kind === "off_list") return null;
    if (f.kind === "model_type") return { leftHead: "State list", left: f.label ?? "", right: f.record ?? "" };
    return { leftHead: "Label", left: f.label ?? "", right: f.record ?? "" };
}

function recordLine(r: PartsRiskRow): string {
    return (
        [r.sizeLabel, r.manufacturer, r.model, (r.assemblyType ?? "").toUpperCase()].filter(Boolean).join(" ") ||
        "nothing on file"
    );
}

export function PartsRiskView({ data, startOpen = false }: { data: PartsRiskResponse; startOpen?: boolean }) {
    // Closed by default: Office Review is a full-height workspace, so this
    // card is a one-line summary until the office opens the list.
    const [open, setOpen] = useState(startOpen);
    const [expanded, setExpanded] = useState(false);
    const { counts } = data;
    const shown = expanded ? data.rows : data.rows.slice(0, 8);
    return (
        <div className="rounded-lg border bg-card p-4 space-y-3" data-testid="parts-risk-card">
            <div>
                <h3 className="text-base font-semibold flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-red-600" />
                    Parts risk: device records that could order the wrong parts
                </h3>
                <p className="text-xs text-foreground/70 mt-0.5">
                    The label, the state list and the record don&apos;t agree. Parts are ordered from the record, so
                    check these first: devices on an open PO, then on an open quote. Open the device, check the tag
                    photo or last report, and fix the record.
                </p>
            </div>
            <div className="flex flex-wrap gap-1.5 text-xs">
                <span className="rounded-full bg-red-600 px-2.5 py-0.5 font-semibold text-white">
                    {counts.onOpenPo.toLocaleString()} on an open PO
                </span>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                    {counts.onOpenQuote.toLocaleString()} on an open quote
                </span>
                <span className="rounded-full bg-red-100 px-2.5 py-0.5 font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-200">
                    {counts.label.toLocaleString()} label ≠ record
                </span>
                <span className="rounded-full bg-red-100 px-2.5 py-0.5 font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-200">
                    {counts.modelType.toLocaleString()} model ≠ type
                </span>
                <span className="rounded-full bg-muted px-2.5 py-0.5 font-semibold text-foreground/70">
                    {counts.offList.toLocaleString()} not on the state list
                </span>
                <span className="px-1 py-0.5 text-foreground/60">
                    of {counts.checked.toLocaleString()} devices checked
                </span>
            </div>
            {!open && (
                <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1">
                    <ChevronDown className="h-3.5 w-3.5" /> Show the list (open POs and quotes first)
                </Button>
            )}
            {open && !data.stateListLoaded && (
                <p className="text-xs text-amber-800 dark:text-amber-200">
                    The state list isn&apos;t loaded, so only label checks ran.
                </p>
            )}
            {open && (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-[10.5px] uppercase tracking-wide text-foreground/60">
                                <th className="py-1.5 pr-2">On order</th>
                                <th className="py-1.5 pr-2">Customer · location</th>
                                <th className="py-1.5 pr-2">Device</th>
                                <th className="py-1.5 pr-2">What doesn&apos;t match</th>
                                <th className="py-1.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((r) => (
                                <tr key={r.id} className="border-t align-top" data-testid="parts-risk-row">
                                    <td className="py-2 pr-2 whitespace-nowrap">
                                        {r.openPo ? (
                                            <Link
                                                to={`/purchasing/${r.openPo.id}/review`}
                                                className="rounded-full bg-red-600 px-2 py-0.5 font-semibold text-white hover:bg-red-700"
                                            >
                                                PO {r.openPo.number ?? ""}
                                            </Link>
                                        ) : r.openQuote ? (
                                            <Link
                                                to={`/estimates/${r.openQuote.id}`}
                                                className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-900 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200"
                                            >
                                                Quote {r.openQuote.number ?? ""}
                                            </Link>
                                        ) : (
                                            <span className="text-foreground/50">—</span>
                                        )}
                                    </td>
                                    <td className="py-2 pr-2">
                                        <div className="font-medium">{r.customerName ?? "—"}</div>
                                        <Link
                                            to={`/sites/${r.siteId}`}
                                            className="text-foreground/70 underline-offset-2 hover:underline"
                                        >
                                            {[r.siteName, r.city].filter(Boolean).join(" · ") || "Location"}
                                        </Link>
                                    </td>
                                    <td className="py-2 pr-2">
                                        <div className="font-medium">{r.name || "No label"}</div>
                                        <div className="text-foreground/70">Record: {recordLine(r)}</div>
                                    </td>
                                    <td className="py-2 pr-2">
                                        <div className="flex flex-col gap-1">
                                            {r.flags.map((f, i) => {
                                                const sides = flagSides(f);
                                                return (
                                                    <div
                                                        key={i}
                                                        className={cn(
                                                            "rounded-md border px-2 py-1",
                                                            f.kind === "off_list"
                                                                ? "border-border bg-muted/50"
                                                                : "border-red-200 bg-red-50 dark:border-red-500/40 dark:bg-red-950/30",
                                                        )}
                                                    >
                                                        <div className="font-semibold text-red-900 dark:text-red-200">
                                                            {f.text}
                                                        </div>
                                                        {sides && (
                                                            <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px]">
                                                                <span>
                                                                    <span className="text-foreground/60">
                                                                        {sides.leftHead}:{" "}
                                                                    </span>
                                                                    <span className="font-semibold text-emerald-800 dark:text-emerald-300">
                                                                        {sides.left}
                                                                    </span>
                                                                </span>
                                                                <span>
                                                                    <span className="text-foreground/60">Record: </span>
                                                                    <span className="font-semibold text-red-700 line-through decoration-red-400 dark:text-red-300">
                                                                        {sides.right}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </td>
                                    <td className="py-2 whitespace-nowrap">
                                        <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                                            <Link to={`/assets/${r.id}`}>Check device</Link>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {open && data.rows.length > 8 && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded((v) => !v)}
                    className="gap-1"
                >
                    {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {expanded
                        ? "Show fewer"
                        : `Show all ${data.rows.length}${counts.total > data.rows.length ? ` of ${counts.total.toLocaleString()}` : ""}`}
                </Button>
            )}
        </div>
    );
}

export function PartsRiskCard() {
    const q = useQuery({
        queryKey: ["assets", "parts-risk"],
        queryFn: () => api.get<PartsRiskResponse>("/api/parts-risk?limit=300"),
        staleTime: 5 * 60_000,
    });
    if (!q.data || q.data.counts.total === 0) return null;
    return <PartsRiskView data={q.data} />;
}
